import React, { useState, useEffect } from 'react';
import {
  CheckCircle,
  XCircle,
  Brain,
  RotateCcw,
  ArrowRight,
  ArrowLeft,
  Clock,
  Target,
  Lightbulb,
  Code,
  BookOpen,
  Layers,
  Settings,
  TrendingUp,
  Star
} from 'lucide-react';

interface QuizQuestion {
  id: string;
  type: 'mcq' | 'design' | 'code' | 'scenario';
  category: 'fundamentals' | 'patterns' | 'implementation' | 'optimization';
  difficulty: 'beginner' | 'intermediate' | 'advanced';
  question: string;
  options?: string[];
  correctAnswer?: number | string;
  explanation: string;
  codeSnippet?: string;
  hints: string[];
  points: number;
  timeLimit?: number; // in seconds
  followUp?: string[];
}

interface QuizResult {
  questionId: string;
  selectedAnswer: number | string | null;
  isCorrect: boolean;
  timeSpent: number;
  hintsUsed: number;
  pointsEarned: number;
}

interface QuizSession {
  questions: QuizQuestion[];
  currentQuestionIndex: number;
  results: QuizResult[];
  startTime: Date;
  timeRemaining: number;
  score: number;
  maxScore: number;
  isCompleted: boolean;
}

const LLDQuiz: React.FC = () => {
  const [session, setSession] = useState<QuizSession | null>(null);
  const [selectedAnswer, setSelectedAnswer] = useState<number | string | null>(null);
  const [showExplanation, setShowExplanation] = useState(false);
  const [usedHints, setUsedHints] = useState<number[]>([]);
  const [questionStartTime, setQuestionStartTime] = useState<Date>(new Date());
  const [quizMode, setQuizMode] = useState<'practice' | 'timed' | 'adaptive'>('practice');
  const [difficulty, setDifficulty] = useState<'beginner' | 'intermediate' | 'advanced'>('beginner');

  const quizQuestions: QuizQuestion[] = [
    {
      id: 'fundamentals-1',
      type: 'mcq',
      category: 'fundamentals',
      difficulty: 'beginner',
      question: 'What is the primary purpose of the Vehicle class in the parking lot system?',
      options: [
        'To store parking spot information',
        'To represent different types of vehicles with their properties',
        'To calculate parking fees only',
        'To manage parking lot levels'
      ],
      correctAnswer: 1,
      explanation: 'The Vehicle class serves as an abstraction to represent different types of vehicles (car, motorcycle, truck) with their common properties like license plate, type, and behavior like calculating spot size and parking fees.',
      hints: [
        'Think about what properties all vehicles share',
        'Consider the abstraction principle in OOP'
      ],
      points: 10,
      timeLimit: 30
    },
    {
      id: 'patterns-1',
      type: 'mcq',
      category: 'patterns',
      difficulty: 'intermediate',
      question: 'Which design pattern is most appropriate for creating different types of vehicles in the parking lot system?',
      options: [
        'Singleton Pattern',
        'Observer Pattern',
        'Factory Pattern',
        'Strategy Pattern'
      ],
      correctAnswer: 2,
      explanation: 'The Factory Pattern is ideal for creating different types of vehicles because it encapsulates object creation logic and allows easy addition of new vehicle types without modifying existing code.',
      codeSnippet: `class VehicleFactory {
  static createVehicle(type, licensePlate) {
    switch (type) {
      case 'car':
        return new Car(licensePlate);
      case 'motorcycle':
        return new Motorcycle(licensePlate);
      case 'truck':
        return new Truck(licensePlate);
      default:
        throw new Error('Unknown vehicle type');
    }
  }
}`,
      hints: [
        'Consider which pattern helps with object creation',
        'Think about how to avoid tight coupling in object creation'
      ],
      points: 15
    },
    {
      id: 'implementation-1',
      type: 'code',
      category: 'implementation',
      difficulty: 'intermediate',
      question: 'Complete the canFitVehicle method in the ParkingSpot class:',
      codeSnippet: `class ParkingSpot {
  constructor(spotId, size, level) {
    this.spotId = spotId;
    this.size = size; // 1=motorcycle, 2=car, 4=truck
    this.level = level;
    this.isOccupied = false;
    this.parkedVehicle = null;
  }
  
  canFitVehicle(vehicle) {
    // Your implementation here
    return false;
  }
}`,
      correctAnswer: 'return !this.isOccupied && vehicle.getSpotSize() <= this.size;',
      explanation: 'The method should check if the spot is not occupied AND if the vehicle can physically fit in the spot (vehicle size <= spot size).',
      hints: [
        'Check if the spot is available first',
        'Compare vehicle size with spot size',
        'Remember smaller vehicles can use larger spots'
      ],
      points: 20
    },
    {
      id: 'optimization-1',
      type: 'scenario',
      category: 'optimization',
      difficulty: 'advanced',
      question: 'Your parking lot system is experiencing slow performance when finding available spots during peak hours. What optimization strategy would you implement?',
      options: [
        'Use linear search through all spots',
        'Maintain separate data structures for available spots by vehicle type',
        'Increase the number of parking levels',
        'Use a database to store all spot information'
      ],
      correctAnswer: 1,
      explanation: 'Maintaining separate data structures (like Sets or Maps) for available spots categorized by vehicle type allows O(1) average-case lookups instead of O(n) linear searches through all spots.',
      codeSnippet: `class Level {
  constructor() {
    this.availableSpots = new Map([
      [1, new Set()], // motorcycle spots
      [2, new Set()], // car spots  
      [4, new Set()]  // truck spots
    ]);
  }
  
  findAvailableSpot(vehicleSize) {
    // O(1) lookup instead of O(n) search
    for (let [spotSize, spots] of this.availableSpots) {
      if (spotSize >= vehicleSize && spots.size > 0) {
        return spots.values().next().value;
      }
    }
    return null;
  }
}`,
      hints: [
        'Think about time complexity of different search approaches',
        'Consider data structures that provide fast lookups',
        'Remember that performance matters during peak usage'
      ],
      points: 25,
      timeLimit: 60
    },
    {
      id: 'patterns-2',
      type: 'mcq',
      category: 'patterns',
      difficulty: 'advanced',
      question: 'To handle real-time notifications when parking spots become available, which pattern would be most appropriate?',
      options: [
        'Command Pattern',
        'Observer Pattern',
        'Decorator Pattern',
        'Adapter Pattern'
      ],
      correctAnswer: 1,
      explanation: 'The Observer Pattern allows multiple interested parties (mobile apps, display boards, reservation systems) to be notified automatically when parking spot availability changes.',
      hints: [
        'Think about one-to-many relationships',
        'Consider automatic notifications',
        'Which pattern handles event broadcasting?'
      ],
      points: 20
    },
    {
      id: 'design-1',
      type: 'design',
      category: 'fundamentals',
      difficulty: 'intermediate',
      question: 'Design a class hierarchy for different types of parking spots (regular, handicapped, VIP, electric vehicle). What would be the best approach?',
      options: [
        'Create separate classes for each spot type with no inheritance',
        'Use composition with a SpotType enum in ParkingSpot class',
        'Create an abstract ParkingSpot class with specific subclasses',
        'Use only interfaces with no concrete classes'
      ],
      correctAnswer: 1,
      explanation: 'Using composition with a SpotType enum is the most flexible approach. It avoids unnecessary inheritance complexity while allowing easy addition of new spot types and different pricing/access rules.',
      codeSnippet: `class ParkingSpot {
  constructor(spotId, size, level, spotType = 'REGULAR') {
    this.spotId = spotId;
    this.size = size;
    this.level = level;
    this.spotType = spotType; // REGULAR, HANDICAPPED, VIP, ELECTRIC
    this.accessRules = this.getAccessRules(spotType);
  }
  
  canPark(vehicle, user) {
    return this.accessRules.canAccess(vehicle, user) && 
           !this.isOccupied && 
           vehicle.getSpotSize() <= this.size;
  }
}`,
      hints: [
        'Consider the principle of composition over inheritance',
        'Think about how easy it would be to add new spot types',
        'Consider the complexity of the inheritance hierarchy'
      ],
      points: 18
    }
  ];

  const initializeQuiz = (mode: 'practice' | 'timed' | 'adaptive', selectedDifficulty: 'beginner' | 'intermediate' | 'advanced') => {
    let questions = [...quizQuestions];
    
    // Filter by difficulty for practice mode
    if (mode === 'practice') {
      questions = questions.filter(q => q.difficulty === selectedDifficulty);
    }
    
    // Shuffle questions for variety
    questions = questions.sort(() => Math.random() - 0.5);
    
    // Limit questions based on mode
    if (mode === 'timed') {
      questions = questions.slice(0, 5); // 5 questions for timed quiz
    } else if (mode === 'adaptive') {
      questions = questions.slice(0, 8); // 8 questions for adaptive
    }

    const maxScore = questions.reduce((sum, q) => sum + q.points, 0);
    const totalTime = mode === 'timed' ? 300 : 0; // 5 minutes for timed mode

    setSession({
      questions,
      currentQuestionIndex: 0,
      results: [],
      startTime: new Date(),
      timeRemaining: totalTime,
      score: 0,
      maxScore,
      isCompleted: false
    });

    setSelectedAnswer(null);
    setShowExplanation(false);
    setUsedHints([]);
    setQuestionStartTime(new Date());
  };

  const submitAnswer = () => {
    if (!session || selectedAnswer === null) return;

    const currentQuestion = session.questions[session.currentQuestionIndex];
    const isCorrect = selectedAnswer === currentQuestion.correctAnswer;
    const timeSpent = (Date.now() - questionStartTime.getTime()) / 1000;
    
    // Calculate points (reduce for hints used)
    let pointsEarned = 0;
    if (isCorrect) {
      pointsEarned = currentQuestion.points;
      // Reduce points for each hint used
      pointsEarned -= usedHints.length * 2;
      pointsEarned = Math.max(pointsEarned, 1); // Minimum 1 point for correct answer
    }

    const result: QuizResult = {
      questionId: currentQuestion.id,
      selectedAnswer,
      isCorrect,
      timeSpent,
      hintsUsed: usedHints.length,
      pointsEarned
    };

    setSession(prev => ({
      ...prev!,
      results: [...prev!.results, result],
      score: prev!.score + pointsEarned
    }));

    setShowExplanation(true);
  };

  const nextQuestion = () => {
    if (!session) return;

    if (session.currentQuestionIndex < session.questions.length - 1) {
      setSession(prev => ({
        ...prev!,
        currentQuestionIndex: prev!.currentQuestionIndex + 1
      }));
      setSelectedAnswer(null);
      setShowExplanation(false);
      setUsedHints([]);
      setQuestionStartTime(new Date());
    } else {
      // Quiz completed
      setSession(prev => ({
        ...prev!,
        isCompleted: true
      }));
    }
  };

  const previousQuestion = () => {
    if (!session || session.currentQuestionIndex === 0) return;
    
    setSession(prev => ({
      ...prev!,
      currentQuestionIndex: prev!.currentQuestionIndex - 1
    }));
    setSelectedAnswer(null);
    setShowExplanation(false);
    setUsedHints([]);
  };

  // Removed unused function: useHint
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const useHint = (hintIndex: number) => {
    if (!usedHints.includes(hintIndex)) {
      setUsedHints(prev => [...prev, hintIndex]);
    }
  };

  const restartQuiz = () => {
    setSession(null);
    setSelectedAnswer(null);
    setShowExplanation(false);
    setUsedHints([]);
  };

  const getScoreColor = (percentage: number) => {
    if (percentage >= 90) return 'text-green-600';
    if (percentage >= 70) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'beginner': return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400';
      case 'intermediate': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400';
      case 'advanced': return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400';
    }
  };

  // Timer effect for timed mode
  useEffect(() => {
    if (session && quizMode === 'timed' && session.timeRemaining > 0 && !session.isCompleted) {
      const timer = setInterval(() => {
        setSession(prev => {
          if (!prev || prev.timeRemaining <= 1) {
            return { ...prev!, timeRemaining: 0, isCompleted: true };
          }
          return { ...prev, timeRemaining: prev.timeRemaining - 1 };
        });
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [session, quizMode]);

  if (!session) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="text-center mb-8">
          <Brain className="w-16 h-16 text-blue-600 mx-auto mb-4" />
          <h2 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-4">
            LLD Knowledge Assessment
          </h2>
          <p className="text-lg text-gray-600 dark:text-gray-400">
            Test your understanding of Low-Level Design concepts through interactive quizzes
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div 
            className={`p-6 rounded-lg border-2 cursor-pointer transition-all ${
              quizMode === 'practice' 
                ? 'border-blue-600 bg-blue-50 dark:bg-blue-900/20' 
                : 'border-gray-200 dark:border-gray-700 hover:border-blue-400'
            }`}
            onClick={() => setQuizMode('practice')}
          >
            <BookOpen className="w-8 h-8 text-blue-600 mb-3" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">Practice Mode</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Learn at your own pace with unlimited time and hints
            </p>
          </div>

          <div 
            className={`p-6 rounded-lg border-2 cursor-pointer transition-all ${
              quizMode === 'timed' 
                ? 'border-orange-600 bg-orange-50 dark:bg-orange-900/20' 
                : 'border-gray-200 dark:border-gray-700 hover:border-orange-400'
            }`}
            onClick={() => setQuizMode('timed')}
          >
            <Clock className="w-8 h-8 text-orange-600 mb-3" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">Timed Challenge</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              5 questions in 5 minutes - test your quick thinking
            </p>
          </div>

          <div 
            className={`p-6 rounded-lg border-2 cursor-pointer transition-all ${
              quizMode === 'adaptive' 
                ? 'border-purple-600 bg-purple-50 dark:bg-purple-900/20' 
                : 'border-gray-200 dark:border-gray-700 hover:border-purple-400'
            }`}
            onClick={() => setQuizMode('adaptive')}
          >
            <TrendingUp className="w-8 h-8 text-purple-600 mb-3" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">Adaptive Test</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Questions adapt to your skill level
            </p>
          </div>
        </div>

        {quizMode === 'practice' && (
          <div className="mb-8">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
              Select Difficulty Level
            </h3>
            <div className="flex space-x-4">
              {['beginner', 'intermediate', 'advanced'].map((level) => (
                <button
                  key={level}
                  onClick={() => setDifficulty(level as any)}
                  className={`px-6 py-3 rounded-lg border-2 transition-all ${
                    difficulty === level
                      ? 'border-blue-600 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300'
                      : 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:border-blue-400'
                  }`}
                >
                  <span className="capitalize font-medium">{level}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="text-center">
          <button
            onClick={() => initializeQuiz(quizMode, difficulty)}
            className="px-8 py-4 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition-colors"
          >
            Start Quiz
          </button>
        </div>
      </div>
    );
  }

  if (session.isCompleted) {
    const percentage = (session.score / session.maxScore) * 100;
    const correctAnswers = session.results.filter(r => r.isCorrect).length;
    const totalTime = session.results.reduce((sum, r) => sum + r.timeSpent, 0);
    const avgTime = totalTime / session.results.length;

    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="text-center mb-8">
          <div className="w-24 h-24 mx-auto mb-4 relative">
            <svg className="w-24 h-24 transform -rotate-90" viewBox="0 0 100 100">
              <circle
                cx="50"
                cy="50"
                r="40"
                stroke="currentColor"
                strokeWidth="8"
                fill="transparent"
                className="text-gray-200 dark:text-gray-700"
              />
              <circle
                cx="50"
                cy="50"
                r="40"
                stroke="currentColor"
                strokeWidth="8"
                fill="transparent"
                strokeDasharray={`${2 * Math.PI * 40}`}
                strokeDashoffset={`${2 * Math.PI * 40 * (1 - percentage / 100)}`}
                className={`transition-all duration-1000 ${getScoreColor(percentage)}`}
                strokeLinecap="round"
              />
            </svg>
            <div className="absolute inset-0 flex items-center justify-center">
              <span className={`text-2xl font-bold ${getScoreColor(percentage)}`}>
                {Math.round(percentage)}%
              </span>
            </div>
          </div>

          <h2 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-2">
            Quiz Completed!
          </h2>
          <p className="text-lg text-gray-600 dark:text-gray-400">
            {percentage >= 90 ? 'Excellent work!' : 
             percentage >= 70 ? 'Good job!' : 
             'Keep practicing!'}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-blue-50 dark:bg-blue-900/30 p-4 rounded-lg border border-blue-200 dark:border-blue-700">
            <div className="flex items-center space-x-2 mb-2">
              <Target className="w-5 h-5 text-blue-600" />
              <span className="text-sm font-medium text-blue-800 dark:text-blue-300">Score</span>
            </div>
            <div className="text-2xl font-bold text-blue-900 dark:text-blue-100">
              {session.score}/{session.maxScore}
            </div>
          </div>

          <div className="bg-green-50 dark:bg-green-900/30 p-4 rounded-lg border border-green-200 dark:border-green-700">
            <div className="flex items-center space-x-2 mb-2">
              <CheckCircle className="w-5 h-5 text-green-600" />
              <span className="text-sm font-medium text-green-800 dark:text-green-300">Correct</span>
            </div>
            <div className="text-2xl font-bold text-green-900 dark:text-green-100">
              {correctAnswers}/{session.questions.length}
            </div>
          </div>

          <div className="bg-purple-50 dark:bg-purple-900/30 p-4 rounded-lg border border-purple-200 dark:border-purple-700">
            <div className="flex items-center space-x-2 mb-2">
              <Clock className="w-5 h-5 text-purple-600" />
              <span className="text-sm font-medium text-purple-800 dark:text-purple-300">Avg Time</span>
            </div>
            <div className="text-2xl font-bold text-purple-900 dark:text-purple-100">
              {Math.round(avgTime)}s
            </div>
          </div>

          <div className="bg-yellow-50 dark:bg-yellow-900/30 p-4 rounded-lg border border-yellow-200 dark:border-yellow-700">
            <div className="flex items-center space-x-2 mb-2">
              <Star className="w-5 h-5 text-yellow-600" />
              <span className="text-sm font-medium text-yellow-800 dark:text-yellow-300">Grade</span>
            </div>
            <div className="text-2xl font-bold text-yellow-900 dark:text-yellow-100">
              {percentage >= 90 ? 'A' : percentage >= 80 ? 'B' : percentage >= 70 ? 'C' : percentage >= 60 ? 'D' : 'F'}
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6 mb-8">
          <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">Question Review</h3>
          <div className="space-y-4">
            {session.questions.map((question, index) => {
              const result = session.results[index];
              return (
                <div key={question.id} className="flex items-center justify-between p-4 border border-gray-200 dark:border-gray-700 rounded-lg">
                  <div className="flex items-center space-x-4">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                      result.isCorrect 
                        ? 'bg-green-100 dark:bg-green-900/30 text-green-600' 
                        : 'bg-red-100 dark:bg-red-900/30 text-red-600'
                    }`}>
                      {result.isCorrect ? <CheckCircle className="w-5 h-5" /> : <XCircle className="w-5 h-5" />}
                    </div>
                    <div>
                      <div className="font-medium text-gray-900 dark:text-gray-100">
                        Question {index + 1}
                      </div>
                      <div className="text-sm text-gray-600 dark:text-gray-400">
                        {question.category} • {question.difficulty}
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-medium text-gray-900 dark:text-gray-100">
                      {result.pointsEarned}/{question.points} pts
                    </div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">
                      {Math.round(result.timeSpent)}s
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="flex justify-center space-x-4">
          <button
            onClick={restartQuiz}
            className="flex items-center space-x-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
          >
            <RotateCcw className="w-5 h-5" />
            <span>Take Another Quiz</span>
          </button>
          <button
            onClick={() => window.location.reload()}
            className="flex items-center space-x-2 px-6 py-3 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Back to Learning</span>
          </button>
        </div>
      </div>
    );
  }

  const currentQuestion = session.questions[session.currentQuestionIndex];
  const progress = ((session.currentQuestionIndex + 1) / session.questions.length) * 100;

  return (
    <div className="max-w-4xl mx-auto p-6">
      {/* Progress Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-4">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
              Question {session.currentQuestionIndex + 1} of {session.questions.length}
            </h2>
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${getDifficultyColor(currentQuestion.difficulty)}`}>
              {currentQuestion.difficulty}
            </span>
            <span className="px-3 py-1 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-full text-sm font-medium">
              {currentQuestion.category}
            </span>
          </div>
          
          <div className="flex items-center space-x-4">
            {quizMode === 'timed' && (
              <div className="flex items-center space-x-2 text-orange-600">
                <Clock className="w-5 h-5" />
                <span className="font-mono text-lg">
                  {Math.floor(session.timeRemaining / 60)}:{(session.timeRemaining % 60).toString().padStart(2, '0')}
                </span>
              </div>
            )}
            <div className="text-sm text-gray-600 dark:text-gray-400">
              Score: {session.score}/{session.maxScore}
            </div>
          </div>
        </div>
        
        <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
          <div 
            className="bg-blue-600 h-2 rounded-full transition-all duration-300"
            style={{ width: `${progress}%` }}
          ></div>
        </div>
      </div>

      {/* Question */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-8 mb-6">
        <div className="flex items-start space-x-4 mb-6">
          <div className="flex-shrink-0">
            {currentQuestion.type === 'mcq' && <Brain className="w-6 h-6 text-blue-600" />}
            {currentQuestion.type === 'code' && <Code className="w-6 h-6 text-green-600" />}
            {currentQuestion.type === 'design' && <Layers className="w-6 h-6 text-purple-600" />}
            {currentQuestion.type === 'scenario' && <Settings className="w-6 h-6 text-orange-600" />}
          </div>
          <div className="flex-1">
            <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">
              {currentQuestion.question}
            </h3>
            
            {currentQuestion.codeSnippet && (
              <div className="bg-gray-900 p-4 rounded-lg mb-6">
                <pre className="text-sm text-gray-100 overflow-x-auto">
                  <code>{currentQuestion.codeSnippet}</code>
                </pre>
              </div>
            )}

            {/* Answer Options */}
            {currentQuestion.options && (
              <div className="space-y-3">
                {currentQuestion.options.map((option, index) => (
                  <button
                    key={index}
                    onClick={() => !showExplanation && setSelectedAnswer(index)}
                    disabled={showExplanation}
                    className={`w-full text-left p-4 rounded-lg border-2 transition-all ${
                      selectedAnswer === index
                        ? showExplanation
                          ? index === currentQuestion.correctAnswer
                            ? 'border-green-500 bg-green-50 dark:bg-green-900/30'
                            : 'border-red-500 bg-red-50 dark:bg-red-900/30'
                          : 'border-blue-500 bg-blue-50 dark:bg-blue-900/30'
                        : showExplanation && index === currentQuestion.correctAnswer
                          ? 'border-green-500 bg-green-50 dark:bg-green-900/30'
                          : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                    }`}
                  >
                    <div className="flex items-center space-x-3">
                      <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                        selectedAnswer === index
                          ? showExplanation
                            ? index === currentQuestion.correctAnswer
                              ? 'border-green-500 bg-green-500 text-white'
                              : 'border-red-500 bg-red-500 text-white'
                            : 'border-blue-500 bg-blue-500 text-white'
                          : showExplanation && index === currentQuestion.correctAnswer
                            ? 'border-green-500 bg-green-500 text-white'
                            : 'border-gray-300 dark:border-gray-600'
                      }`}>
                        {(showExplanation && index === currentQuestion.correctAnswer) ||
                         (selectedAnswer === index && showExplanation && index === currentQuestion.correctAnswer) ? (
                          <CheckCircle className="w-4 h-4" />
                        ) : selectedAnswer === index && showExplanation && index !== currentQuestion.correctAnswer ? (
                          <XCircle className="w-4 h-4" />
                        ) : selectedAnswer === index ? (
                          <div className="w-2 h-2 bg-white rounded-full" />
                        ) : null}
                      </div>
                      <span className="text-gray-700 dark:text-gray-300">{option}</span>
                    </div>
                  </button>
                ))}
              </div>
            )}

            {/* Code Answer Input */}
            {currentQuestion.type === 'code' && !currentQuestion.options && (
              <div className="space-y-4">
                <textarea
                  value={selectedAnswer as string || ''}
                  onChange={(e) => setSelectedAnswer(e.target.value)}
                  placeholder="Enter your code implementation..."
                  disabled={showExplanation}
                  className="w-full h-32 p-4 font-mono text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-gray-100 disabled:opacity-50"
                />
              </div>
            )}
          </div>
        </div>

        {/* Hints */}
        {currentQuestion.hints.length > 0 && !showExplanation && (
          <div className="mb-6">
            <h4 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3 flex items-center">
              <Lightbulb className="w-5 h-5 mr-2 text-yellow-600" />
              Hints
            </h4>
            <div className="space-y-2">
              {currentQuestion.hints.map((hint, index) => (
                <div key={index}>
                  {usedHints.includes(index) ? (
                    <div className="p-3 bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-200 dark:border-yellow-700 rounded-lg">
                      <p className="text-yellow-800 dark:text-yellow-300">{hint}</p>
                    </div>
                  ) : (
                    <button
                      onClick={() => setUsedHints(prev => [...prev, index])}
                      className="w-full text-left p-3 border border-yellow-200 dark:border-yellow-700 rounded-lg hover:bg-yellow-50 dark:hover:bg-yellow-900/30 transition-colors"
                    >
                      <span className="text-yellow-700 dark:text-yellow-400">💡 Click for hint #{index + 1}</span>
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Explanation */}
        {showExplanation && (
          <div className="mt-6 p-6 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-700 rounded-lg">
            <h4 className="text-lg font-semibold text-blue-800 dark:text-blue-300 mb-3">Explanation</h4>
            <p className="text-blue-700 dark:text-blue-400 mb-4">{currentQuestion.explanation}</p>
            
            {currentQuestion.followUp && (
              <div>
                <h5 className="font-semibold text-blue-800 dark:text-blue-300 mb-2">Follow-up Questions:</h5>
                <ul className="list-disc list-inside space-y-1 text-blue-700 dark:text-blue-400">
                  {currentQuestion.followUp.map((question, index) => (
                    <li key={index}>{question}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Action Buttons */}
      <div className="flex items-center justify-between">
        <button
          onClick={previousQuestion}
          disabled={session.currentQuestionIndex === 0}
          className="flex items-center space-x-2 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Previous</span>
        </button>

        <div className="flex items-center space-x-4">
          <div className="text-sm text-gray-600 dark:text-gray-400">
            {currentQuestion.points} points
          </div>
          
          {!showExplanation ? (
            <button
              onClick={submitAnswer}
              disabled={selectedAnswer === null}
              className="flex items-center space-x-2 px-6 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white rounded-lg transition-colors"
            >
              <span>Submit Answer</span>
            </button>
          ) : (
            <button
              onClick={nextQuestion}
              className="flex items-center space-x-2 px-6 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors"
            >
              <span>
                {session.currentQuestionIndex === session.questions.length - 1 ? 'Finish Quiz' : 'Next Question'}
              </span>
              <ArrowRight className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default LLDQuiz;
